import { useState } from 'react'
import { DuffelClient } from "@/lib/duffel-client"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import type { Flight } from "@/types/travel"

const TEST_ROUTES = [
  { origin: 'LHR', destination: 'JFK', description: 'London → New York' },
  { origin: 'LAX', destination: 'SFO', description: 'Los Angeles → San Francisco' },
  { origin: 'CDG', destination: 'FCO', description: 'Paris → Rome' },
  { origin: 'SIN', destination: 'SYD', description: 'Singapore → Sydney' },
]

export default function DuffelTestPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [flights, setFlights] = useState<Flight[]>([])
  const [selectedRoute, setSelectedRoute] = useState(TEST_ROUTES[0])
  const [departureDate, setDepartureDate] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() + 30)
    return date.toISOString().split('T')[0]
  })
  const [returnDate, setReturnDate] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() + 37)
    return date.toISOString().split('T')[0]
  })
  const [adults, setAdults] = useState(1)
  const [children, setChildren] = useState(0)
  const [responseTime, setResponseTime] = useState<number | null>(null)

  const handleTest = async () => {
    setIsLoading(true)
    setError(null)
    setFlights([])
    setResponseTime(null)

    try {
      const startTime = performance.now()
      
      // Test flight search with selected data
      const testFlights = await DuffelClient.searchFlights({
        origin: selectedRoute.origin,
        destination: selectedRoute.destination,
        departureDate: departureDate,
        returnDate: returnDate,
        passengers: {
          adults: adults,
          children: children
        }
      })

      const endTime = performance.now()
      setResponseTime(endTime - startTime)
      setFlights(testFlights)
    } catch (err) {
      console.error('Duffel API Error:', err)
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-2xl font-bold mb-6">Duffel API Test Page</h1>
      
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <Label>Test Route</Label>
              <Select
                value={selectedRoute.origin + '-' + selectedRoute.destination}
                onValueChange={(value: string) => {
                  const [origin, destination] = value.split('-')
                  setSelectedRoute(TEST_ROUTES.find(r => r.origin === origin && r.destination === destination) || TEST_ROUTES[0])
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a route" />
                </SelectTrigger>
                <SelectContent>
                  {TEST_ROUTES.map((route) => (
                    <SelectItem key={route.origin + '-' + route.destination} value={route.origin + '-' + route.destination}>
                      {route.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Departure Date</Label>
                <Input
                  type="date"
                  value={departureDate}
                  onChange={(e) => setDepartureDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div>
                <Label>Return Date</Label>
                <Input
                  type="date"
                  value={returnDate}
                  onChange={(e) => setReturnDate(e.target.value)}
                  min={departureDate}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Adults</Label>
                <Input
                  type="number"
                  min="1"
                  max="9"
                  value={adults}
                  onChange={(e) => setAdults(parseInt(e.target.value))}
                />
              </div>
              <div>
                <Label>Children</Label>
                <Input
                  type="number"
                  min="0"
                  max="8"
                  value={children}
                  onChange={(e) => setChildren(parseInt(e.target.value))}
                />
              </div>
            </div>
            
            <Button 
              onClick={handleTest}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? 'Searching...' : 'Search Flights'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="mb-6 border-red-500">
          <CardContent className="pt-6">
            <div className="text-red-500">
              <h2 className="font-bold mb-2">Error</h2>
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {flights.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold">Test Results</h2>
              {responseTime && (
                <span className="text-sm text-gray-500">
                  Response time: {responseTime.toFixed(0)}ms
                </span>
              )}
            </div>
            <div className="space-y-4">
              {flights.map((flight, index) => (
                <div key={index} className="border rounded-md p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{flight.airline}</p>
                      <p className="text-sm text-gray-500">
                        {flight.origin} → {flight.destination}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(flight.departureTime).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">${flight.price}</p>
                      <p className="text-sm text-gray-500">
                        {flight.cabinClass}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
} 